/* SSW adapter for Core's public product-action contract. No orders or checkout. */
(() => {
  'use strict';
  if (window.__sswAliigoCartInstalled) return;
  window.__sswAliigoCartInstalled = true;
  const target = '#ssw-aliigo-position > aliigo-widget';
  const storageKey = 'ssw:aliigo:cart-actions:v1';
  const inFlight = new Map();
  const failed = () => ({ status: 'failed' });
  const unknown = () => ({ status: 'unknown' });
  const loadLedger = () => {
    const value = JSON.parse(sessionStorage.getItem(storageKey) || '[]');
    if (!Array.isArray(value) || value.length > 100) throw new Error('Invalid action ledger');
    return value;
  };
  const saveRecord = record => {
    const records = loadLedger();
    const index = records.findIndex(item => item.activationId === record.activationId);
    if (index < 0) {
      if (records.length >= 100) throw new Error('Action ledger full');
      records.push(record);
    } else records[index] = record;
    sessionStorage.setItem(storageKey, JSON.stringify(records));
  };
  // Keep v1 fingerprints readable: earlier pending records must remain blocking.
  const hasUncertainVariant = (records, action) => records.some(record => {
    if (record.status !== 'pending' && record.status !== 'unknown') return false;
    const identity = JSON.parse(record.fingerprint);
    if (!Array.isArray(identity) || identity.length < 2) throw new Error('Invalid action identity');
    return identity[0] === action.productId && identity[1] === action.variantId;
  });
  const normalize = request => {
    if (!request || typeof request.activationId !== 'string' ||
        !/^[a-zA-Z0-9_-]{1,128}$/.test(request.activationId) || request.quantity !== 1 ||
        typeof request.productId !== 'string' || !/^gid:\/\/shopify\/Product\/\d+$/.test(request.productId) ||
        typeof request.variantId !== 'string' || !/^\d+$/.test(request.variantId) ||
        typeof request.url !== 'string' || !Number.isSafeInteger(request.quotedPriceMinor) ||
        request.quotedPriceMinor < 0 || request.currency !== 'USD') return null;
    const url = new URL(request.url);
    if (url.origin !== location.origin || url.username || url.password || url.hash ||
        !/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?products\/[a-z0-9][a-z0-9-]*\/?$/i.test(url.pathname)) return null;
    for (const [key, value] of url.searchParams) {
      if (key !== 'variant' || value !== request.variantId) return null;
    }
    const root = window.Shopify?.routes?.root || '/';
    if (typeof root !== 'string' || !/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?$/i.test(root)) return null;
    const productId = request.productId.split('/').pop();
    const productUrl = new URL(url.pathname.replace(/\/$/, '') + '.js', location.origin).href;
    const cartUrl = new URL(root + 'cart', location.origin).href;
    const addUrl = new URL(root + 'cart/add.js', location.origin).href;
    const cartReadUrl = new URL(root + 'cart.js', location.origin).href;
    const fingerprint = JSON.stringify([productId, request.variantId, productUrl, root, 1, request.quotedPriceMinor, request.currency]);
    return { activationId: request.activationId, productId, variantId: request.variantId,
      productUrl, cartUrl, addUrl, cartReadUrl, fingerprint,
      quotedPriceMinor: request.quotedPriceMinor, currency: request.currency };
  };
  const fetchWithTimeout = async (url, options) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(url, { ...options, credentials: 'same-origin', redirect: 'error', signal: controller.signal });
      return { ok: response.ok, status: response.status, data: await response.json() };
    }
    finally { clearTimeout(timer); }
  };
  const execute = async action => {
    let prior, records;
    try { records = loadLedger(); prior = records.find(item => item.activationId === action.activationId); }
    catch (_) { return failed(); }
    if (prior) {
      if (prior.fingerprint !== action.fingerprint) return failed();
      if (prior.status === 'added') return { status: 'added', cartUrl: action.cartUrl };
      return prior.status === 'failed' ? failed() : unknown();
    }
    try { if (hasUncertainVariant(records, action)) return unknown(); }
    catch (_) { return failed(); }
    let product;
    try {
      const response = await fetchWithTimeout(action.productUrl, { method: 'GET', cache: 'no-store', headers: { Accept: 'application/json' } });
      if (!response.ok) return failed();
      product = response.data;
    } catch (_) { return failed(); }
    const variant = Array.isArray(product?.variants) && product.variants.length === 1 ? product.variants[0] : null;
    if (String(product?.id) !== action.productId || !variant || String(variant.id) !== action.variantId ||
        product.available !== true || variant.available !== true || product.requires_selling_plan || variant.requires_selling_plan ||
        !Array.isArray(product.selling_plan_groups) || product.selling_plan_groups.length !== 0 ||
        !Array.isArray(variant.selling_plan_allocations) || variant.selling_plan_allocations.length !== 0 ||
        !Number.isSafeInteger(variant.price) || variant.price !== action.quotedPriceMinor) return failed();
    // These are guards against stale cards, never authority to set a price/currency.
    // Inspect only the currency; never persist/log the visitor's cart contents.
    try {
      const cart = await fetchWithTimeout(action.cartReadUrl, { method: 'GET', cache: 'no-store', headers: { Accept: 'application/json' } });
      if (!cart.ok || cart.data?.currency !== action.currency) return failed();
    } catch (_) { return failed(); }
    const record = { activationId: action.activationId, fingerprint: action.fingerprint, status: 'pending' };
    // Persist before the POST: a reload after an uncertain response must not retry.
    try {
      // Recheck after async reads. Check + pending write have no await between them,
      // so different activation IDs in this page cannot both acquire the variant.
      if (hasUncertainVariant(loadLedger(), action)) return unknown();
      saveRecord(record);
    } catch (_) { return failed(); }
    let result;
    try {
      const response = await fetchWithTimeout(action.addUrl, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: action.variantId, quantity: 1 }] })
      });
      if (!response.ok) {
        // Only a structured Shopify validation rejection establishes no add.
        // Server/proxy errors and other ambiguous responses may follow a mutation.
        const rejected = response.status === 422 && response.data?.status === 422 &&
          typeof response.data.description === 'string' && response.data.description.trim().length > 0;
        result = rejected ? failed() : unknown();
      }
      else {
        const data = response.data;
        const item = Array.isArray(data?.items) && data.items.length === 1 ? data.items[0] : null;
        result = item && String(item.product_id) === action.productId &&
          String(item.variant_id ?? item.id) === action.variantId &&
          Number.isInteger(item.quantity) && item.quantity >= 1 && !item.selling_plan_allocation
          ? { status: 'added', cartUrl: action.cartUrl } : unknown();
      }
    } catch (_) { result = unknown(); }
    // No automatic retry, fake theme success event, cart clear or navigation.
    try { saveRecord({ ...record, status: result.status }); } catch (_) { /* pending stays conservative on reload */ }
    return result;
  };
  const handler = request => {
    let action;
    try { action = normalize(request); } catch (_) { return Promise.resolve(failed()); }
    if (!action) return Promise.resolve(failed());
    const existing = inFlight.get(action.activationId);
    if (existing) return existing.fingerprint === action.fingerprint ? existing.promise : Promise.resolve(failed());
    if (inFlight.size >= 100) return Promise.resolve(failed());
    const promise = execute(action).catch(() => unknown());
    inFlight.set(action.activationId, { fingerprint: action.fingerprint, promise });
    return promise;
  };
  let attempts = 0;
  const register = () => {
    if (window.AliigoWidget?.setProductActionHandler?.(handler, target)) return;
    if (++attempts < 120) setTimeout(register, 1000);
  };
  register();
})();
